import { Link } from "react-router-dom";
import { Instagram, Linkedin, Phone, Mail, MapPin, Clock } from "lucide-react";
import { BRAND, SERVICES, LEGAL_PAGES } from "@/lib/site-data";

export default function Footer() {
  const solutionLinks = [
    { slug: "pazaryeri-kurulum-yonetimi", label: "Pazaryeri Yönetimi" },
    {
      slug: "uye-isyerine-ozel-siparis-sistemi",
      label: "Web ve Mobil Sipariş Sistemi",
    },
    { slug: "kurye-cozumu", label: "Kurye Çözümü" },
    { slug: "fiyat-ve-stok-entegrasyonu", label: "Fiyat-Stok Entegrasyonu" },
    { slug: "dijital-operasyon-yonetimi", label: "Dijital Operasyon Yönetimi" },
  ];

  const corporateLinks = [
    { href: "/hakkimizda", label: "Hakkımızda" },
    { href: "/referanslarimiz", label: "Referanslarımız" },
    { href: "/nasil-calisir", label: "Sık Sorulan Sorular" },
    { href: "/iletisim", label: "İletişim" },
  ];

  return (
    <footer
      className="bg-[#070807] border-t border-white/[0.06] mt-20"
      data-testid="site-footer"
      style={{ paddingBottom: "max(0px, env(safe-area-inset-bottom))" }}
    >
      <div className="ep-container py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <Link to="/" className="inline-flex items-center mb-4" aria-label="Epersonel Ana Sayfa">
            <span className="ep-logo-wrap" style={{ width: 180, height: 36 }}>
              <img
                src={BRAND.logo}
                alt={BRAND.logoAlt}
                className="ep-logo-img"
                width="500"
                height="500"
              />
            </span>
          </Link>
          <p className="text-sm text-muted leading-relaxed">
            Epersonel; Üye İşyerleri için pazaryeri kurulumu ve yönetimi, web ve
            mobil sipariş sistemi, fiyat-stok entegrasyonu, dijital operasyon ve
            kurye çözümleri sunar.
          </p>
        </div>

        <div>
          <h3 className="font-display font-semibold text-white mb-4 text-sm uppercase tracking-widest">
            Çözümler
          </h3>
          <ul className="space-y-2.5 text-sm">
            {solutionLinks.map((l) => (
              <li key={l.slug}>
                <Link
                  to={`/hizmetlerimiz/${l.slug}`}
                  className="text-muted hover:text-[#00F22E] transition-colors"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-display font-semibold text-white mb-4 text-sm uppercase tracking-widest">
            Kurumsal
          </h3>
          <ul className="space-y-2.5 text-sm">
            {corporateLinks.map((l) => (
              <li key={l.href}>
                <Link
                  to={l.href}
                  className="text-muted hover:text-[#00F22E] transition-colors"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
          <h3 className="font-display font-semibold text-white mt-6 mb-4 text-sm uppercase tracking-widest">
            Yasal
          </h3>
          <ul className="space-y-2.5 text-sm">
            {LEGAL_PAGES.map((l) => (
              <li key={l.slug}>
                <Link
                  to={`/yasal/${l.slug}`}
                  className="text-muted hover:text-[#00F22E] transition-colors"
                  data-testid={`footer-legal-${l.slug}`}
                >
                  {l.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-display font-semibold text-white mb-4 text-sm uppercase tracking-widest">
            İletişim
          </h3>
          <ul className="space-y-3 text-sm text-muted">
            <li className="flex items-start gap-2">
              <Phone className="h-4 w-4 mt-0.5 text-[#00F22E]" />
              <a
                href={`tel:+${BRAND.contact.phoneRaw}`}
                className="hover:text-[#00F22E] transition-colors"
              >
                {BRAND.contact.phone}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <Mail className="h-4 w-4 mt-0.5 text-[#00F22E]" />
              <a
                href={`mailto:${BRAND.contact.email}`}
                className="hover:text-[#00F22E] transition-colors"
              >
                {BRAND.contact.email}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <MapPin className="h-4 w-4 mt-0.5 text-[#00F22E]" />
              {BRAND.contact.location}
            </li>
            <li className="flex items-start gap-2">
              <Clock className="h-4 w-4 mt-0.5 text-[#00F22E]" />
              {BRAND.contact.hours}
            </li>
          </ul>
          <div className="flex items-center gap-3 mt-5">
            <a
              href={`https://instagram.com/${BRAND.contact.instagram}`}
              className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/10 text-white/80 hover:text-[#00F22E] hover:border-[#00F22E] transition-colors"
              aria-label="Instagram"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
            >
              <Instagram className="h-4 w-4" />
            </a>
            <a
              href={`https://linkedin.com/company/${BRAND.contact.linkedin}`}
              className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/10 text-white/80 hover:text-[#00F22E] hover:border-[#00F22E] transition-colors"
              aria-label="LinkedIn"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-linkedin"
            >
              <Linkedin className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
      <div className="border-t border-white/[0.06]">
        <div className="ep-container py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-muted">
          <p>© 2026 Epersonel. Tüm hakları saklıdır.</p>
          <button
            type="button"
            onClick={() => {
              window.dispatchEvent(new Event("epersonel:open-cookie-settings"));
            }}
            className="hover:text-[#00F22E] transition-colors"
            data-testid="footer-cookie-settings"
          >
            Çerez Tercihleri
          </button>
        </div>
      </div>
    </footer>
  );
}
